<template>
  <b-modal
    :id="modalId"
    :title="isEditing ? 'Edit Task' : 'Add New Task'"
    size="lg"
    centered
    @hidden="resetModal"
    no-enforce-focus
  >
    <b-form @submit.prevent="handleSubmit">
      <!-- Title -->
      <b-form-group label="Title" label-for="task-title" label-class="form-label">
        <b-form-input id="task-title" v-model="formData.title" placeholder="Enter task title" required />
        <small v-if="isTitleInvalid" class="text-danger mt-1">This field is required.</small>
      </b-form-group>

      <!-- Description -->
      <b-form-group label="Description" label-for="task-description" label-class="form-label">
        <editor
          id="task-description"
          name="task-description"
          :key="'desc-editor-' + editorKey"
          :init="{
            height: 300,
            menubar: 'edit view insert format tools table help',
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount',
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
             alignleft aligncenter alignright alignjustify | \
             bullist numlist outdent indent | removeformat | help',
          }"
          v-model="formData.description"
          placeholder="Enter task description"
        />
      </b-form-group>

      <!-- URL -->
      <b-form-group :state="formData.url ? !urlError : null">
        <label for="task-url" class="form-label"
          >Resource URL<i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top
            title="Include Reference Materials — Provide relevant URLs for video tutorials, official documentation or other helpful learning resources."
            >info</i
          ></label
        >
        <b-form-input
          id="task-url"
          v-model="formData.url"
          type="url"
          placeholder="https://example.com/resource"
          :state="formData.url ? !urlError : null"
          @input="validateUrl"
        />
        <template v-if="formData.url && urlError" #invalid-feedback>
          {{ urlError }}
        </template>
      </b-form-group>

      <!-- Resource Button Text -->
      <b-form-group v-if="formData.url">
        <label for="task-url-label" class="form-label"
          >Resource Button Text<i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top
            title="Customize the label shown on the resource link (e.g. 'Click here', 'Watch video'). Defaults to 'View Resource' when left blank."
            >info</i
          ></label
        >
        <b-form-input
          id="task-url-label"
          v-model="formData.url_label"
          maxlength="100"
          placeholder="View Resource"
        />
      </b-form-group>

      <!-- Case Study Scenario (only shown when completion_mode is case_study) -->
      <b-form-group
        v-if="formData.completion_mode === 'case_study'"
        label="Case Study Scenario"
        label-for="task-case-study"
        label-class="form-label"
        description="Shown to the student as read-only reference on the left side of the response modal."
      >
        <editor
          id="task-case-study"
          name="task-case-study"
          :key="'case-study-editor-' + editorKey"
          :init="{
            height: 400,
            menubar: 'edit view insert format tools table help',
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount',
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
             alignleft aligncenter alignright alignjustify | \
             bullist numlist outdent indent | removeformat | help',
          }"
          v-model="formData.case_study_content"
          placeholder="Enter the case study scenario the student should read"
        />
      </b-form-group>

      <!-- Case Study Questions (only shown when completion_mode is case_study) -->
      <b-form-group
        v-if="formData.completion_mode === 'case_study'"
        label="Answer the Following (Questions)"
        label-for="task-case-questions"
        label-class="form-label"
        description="Pre-populated into the student's response editor so they can type answers between the questions."
      >
        <editor
          id="task-case-questions"
          name="task-case-questions"
          :key="'case-questions-editor-' + editorKey"
          :init="{
            height: 300,
            menubar: 'edit view insert format tools table help',
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount',
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
             alignleft aligncenter alignright alignjustify | \
             bullist numlist outdent indent | removeformat | help',
          }"
          v-model="formData.case_study_questions"
          placeholder="e.g. 1. What would you do first? 2. Why?"
        />
      </b-form-group>

      <!-- Scenario Interview (scenario assignments only) -->
      <b-form-group
        v-if="isScenarioTask"
        label="School Scenario Interview"
        label-for="task-simulation"
        label-class="form-label"
      >
        <v-select
          id="task-simulation"
          class="form-control v-select-custom"
          label="title"
          v-model="formData.simulation"
          :reduce="option => option.id"
          placeholder="Select a school scenario interview"
          :options="scenarioInterviews"
          :loading="areInterviewsLoading"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" v-bind="attributes" v-on="events" />
          </template>
          <template slot="option" slot-scope="option">
            <span>{{ option.title }}</span>
          </template>
        </v-select>
        <small class="form-text text-muted">
          Only school scenario interviews published for this assignment's school and program are listed. The task is
          marked complete automatically once the student finishes the interview call.
        </small>
        <small v-if="simulationInvalid" class="text-danger d-block mt-1">
          Select an interview for this scenario task.
        </small>
        <b-alert v-if="!areInterviewsLoading && !scenarioInterviews.length" show variant="warning" class="mt-2 mb-0">
          No school scenario interviews are available for this school and program yet. A super admin creates them under
          Interviews.
        </b-alert>
      </b-form-group>

      <!-- Completion Mode -->
      <b-form-group
        v-if="!isScenarioTask"
        label="Completion Mode"
        label-for="task-completion-mode"
        label-class="form-label"
      >
        <b-form-radio-group
          id="task-completion-mode"
          v-model="formData.completion_mode"
          :options="completionModes"
          name="completion-mode"
          required
        ></b-form-radio-group>
      </b-form-group>

      <!-- Grading: task_marks + rubric (graded assignments only) -->
      <template v-if="isGradedAssignment">
        <b-form-group v-if="isAiGradingEnabled" label="Task Marks" label-for="task-marks" label-class="form-label">
          <b-form-input
            id="task-marks"
            v-model.number="formData.task_marks"
            type="number"
            min="0"
            step="0.5"
            placeholder="e.g. 15"
          />
          <small class="form-text text-muted">
            Marks awarded for this task. The sum across all tasks should equal the assignment's Total Marks.
          </small>
        </b-form-group>

        <template v-if="formData.completion_mode === 'checkbox'">
          <b-form-group label="Verified action" label-class="form-label">
            <b-form-select
              v-model="formData.checkbox_action_abbr"
              :options="verifiedActionOptions"
              :disabled="isLoadingCheckboxActions"
              :state="checkboxAbbrState"
              @change="handleCheckboxActionChange"
            />
            <small v-if="selectedCheckboxActionDescription" class="form-text text-muted">
              {{ selectedCheckboxActionDescription }}
            </small>
            <small class="form-text text-muted">
              Leave 'Verified action' set to 'None' to award full marks as soon as the student ticks this task. Pick an
              action to require the student to actually complete it on the portal first.
            </small>
            <small v-if="checkboxAbbrError" class="text-danger d-block mt-1">{{ checkboxAbbrError }}</small>
          </b-form-group>

          <b-form-group v-if="selectedActionParams.length" label="Action parameters" label-class="form-label">
            <div v-for="paramKey in selectedActionParams" :key="paramKey" class="mb-2">
              <label class="form-label mb-1">
                {{ getParamLabel(paramKey) }}
                <small v-if="isOptionalParam(paramKey)" class="text-muted">(optional)</small>
              </label>
              <b-form-select
                v-if="paramKey === 'program_id'"
                :options="getProgramSelectOptions()"
                :value="getParamValue(paramKey)"
                :disabled="paramLoadingMap[paramKey]"
                @input="setParamValue(paramKey, $event)"
              />
              <b-form-select
                v-else-if="getParamChoiceOptions(paramKey).length"
                :options="getParamChoiceOptions(paramKey)"
                :value="getParamValue(paramKey)"
                @input="setParamValue(paramKey, $event)"
              />
              <v-select
                v-else-if="isKnownParam(paramKey)"
                class="v-select-custom"
                :options="getParamOptions(paramKey)"
                :reduce="opt => opt.id"
                label="label"
                :value="getParamValue(paramKey)"
                :placeholder="`Select ${getParamLabel(paramKey).toLowerCase()}`"
                :loading="paramLoadingMap[paramKey]"
                @input="setParamValue(paramKey, $event)"
              />
              <b-form-input
                v-else
                :value="getParamValue(paramKey)"
                :placeholder="`Enter ${getParamLabel(paramKey).toLowerCase()}`"
                @input="setParamValue(paramKey, $event)"
              />
            </div>
            <small v-if="checkboxParamsError" class="text-danger d-block mt-1">{{ checkboxParamsError }}</small>
          </b-form-group>

          <b-form-group label-class="form-label">
            <div class="alert alert-info mb-0 py-2">
              <i class="material-icons icon-16pt mr-1">info</i>
              Checkbox tasks award full marks on acknowledgement when no verified action is selected.
            </div>
          </b-form-group>
        </template>

        <template v-if="isAiGradingEnabled && formData.completion_mode !== 'checkbox'">
          <b-form-group label-class="form-label">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <label class="form-label mb-0">
                Task Rubric
                <span class="text-danger" title="Required when AI grading is enabled">*</span>
              </label>
              <b-dropdown size="sm" variant="outline-secondary" text="Use template">
                <b-dropdown-item-button
                  v-for="opt in taskTemplates"
                  :key="opt.value"
                  @click="insertRubricTemplate(opt)"
                >{{
                  opt.label
                }}</b-dropdown-item-button>
              </b-dropdown>
            </div>
            <b-form-textarea
              v-model="formData.rubric"
              rows="12"
              class="monospace-textarea"
              :class="rubricInvalid ? 'is-invalid' : ''"
              placeholder="Per-task rubric for AI grading."
            />
            <small class="form-text text-muted">
              Required when AI auto-grading is enabled.
            </small>
            <div v-if="rubricInvalid" class="invalid-feedback d-block">
              A rubric is required when AI auto-grading is enabled.
            </div>

          </b-form-group>
        </template>
      </template>
    </b-form>

    <template #modal-footer>
      <div class="w-100">
        <b-button variant="primary" class="float-right mr-2" @click="handleSubmit" :disabled="isSubmitting">
          <b-spinner small v-if="isSubmitting"></b-spinner>
          {{ isEditing ? 'Save' : 'Add' }} Task
        </b-button>
        <b-button variant="outline-secondary" class="float-right mr-2" @click="$bvModal.hide(modalId)">
          Cancel
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from 'axios';
import Editor from '@tinymce/tinymce-vue';
import { required } from 'vuelidate/lib/validators';
import { RUBRIC_TEMPLATE_OPTIONS } from '@/common/aiGradingTemplates';

const CHECKBOX_NONE_VALUE = '';
const PORTAL_CUSTOM_ACTION = {
  abbr: 'portal_custom_action',
  label: 'Portal — Custom action (always passes)',
  description:
    'Always passes — full task marks are awarded as soon as the student marks this task done. Use for ad-hoc tasks where no portal-side state exists.',
  scope: 'portal',
  required_params: [],
};
const CHECKBOX_ACTION_CACHE = {
  items: null,
  promise: null,
};

export default {
  name: 'TaskModal',
  components: {
    Editor,
  },
  props: {
    modalId: {
      type: String,
      required: true,
      default: 'task-modal',
    },
    task: {
      type: Object,
      default: () => ({
        title: '',
        description: '',
        url: '',
        url_label: '',
        completion_mode: 'checkbox',
        case_study_content: '',
        case_study_questions: '',
        task_marks: 0,
        rubric: '',
      }),
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    isGradedAssignment: {
      type: Boolean,
      default: false,
    },
    isAiGradingEnabled: {
      type: Boolean,
      default: false,
    },
    assignmentId: {
      type: [String, Number],
      default: null,
    },
    assignmentType: {
      type: String,
      default: 'standard',
    },
    programId: {
      type: [String, Number],
      default: null,
    },
    serverErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      editorKey: 0,
      formData: {
        title: '',
        description: '',
        url: '',
        url_label: '',
        completion_mode: 'checkbox',
        case_study_content: '',
        case_study_questions: '',
        task_marks: 0,
        rubric: '',
        checkbox_action_abbr: CHECKBOX_NONE_VALUE,
        checkbox_action_params: {},
        simulation: null,
      },
      scenarioInterviews: [],
      areInterviewsLoading: false,
      simulationInvalid: false,
      completionModes: [
        { text: 'Checkbox', value: 'checkbox' },
        { text: 'File Upload', value: 'upload' },
        { text: 'Text', value: 'text' },
        { text: 'Case Study', value: 'case_study' },
      ],
      isSubmitting: false,
      isTitleInvalid: false,
      rubricInvalid: false,
      urlError: '',
      checkboxActions: [],
      isLoadingCheckboxActions: false,
      optionsByParam: {
        program_id: [],
      },
      paramLoadingMap: {
        program_id: false,
      },
      paramLoadPromises: {
        program_id: null,
      },
      checkboxFieldErrors: {
        checkbox_action_abbr: '',
        checkbox_action_params: '',
      },
    };
  },
  validations() {
    return {
      formData: {
        title: { required },
        completion_mode: { required },
      },
    };
  },
  computed: {
    isScenarioTask() {
      return this.assignmentType === 'scenario';
    },
    taskTemplates() {
      const mode = this.formData.completion_mode;
      const templateByMode = {
        text: ['generic', 'text'],
        case_study: ['generic', 'case_study'],
        upload: ['upload'],
      };
      const allowedValues = templateByMode[mode];
      if (!allowedValues) {
        return RUBRIC_TEMPLATE_OPTIONS.filter(o => o.scope === 'task');
      }
      return RUBRIC_TEMPLATE_OPTIONS.filter(o => o.scope === 'task' && allowedValues.includes(o.value));
    },
    normalizedCheckboxActions() {
      const items = Array.isArray(this.checkboxActions) ? [...this.checkboxActions] : [];
      const hasCustom = items.some(item => item && item.abbr === PORTAL_CUSTOM_ACTION.abbr);
      if (!hasCustom) items.push(PORTAL_CUSTOM_ACTION);
      return items;
    },
    selectedCheckboxAction() {
      if (!this.formData.checkbox_action_abbr) return null;
      return this.normalizedCheckboxActions.find(action => action.abbr === this.formData.checkbox_action_abbr) || null;
    },
    selectedCheckboxActionDescription() {
      return this.selectedCheckboxAction ? this.selectedCheckboxAction.description : '';
    },
    selectedRequiredParams() {
      const params = this.selectedCheckboxAction && Array.isArray(this.selectedCheckboxAction.required_params)
        ? this.selectedCheckboxAction.required_params
        : [];
      return params;
    },
    selectedOptionalParams() {
      const params = this.selectedCheckboxAction && Array.isArray(this.selectedCheckboxAction.optional_params)
        ? this.selectedCheckboxAction.optional_params
        : [];
      return params;
    },
    selectedActionParams() {
      return [...this.selectedRequiredParams, ...this.selectedOptionalParams];
    },
    selectedActionParamChoices() {
      const choices = this.selectedCheckboxAction && this.selectedCheckboxAction.param_choices;
      return choices && typeof choices === 'object' ? choices : {};
    },
    portalCheckboxActions() {
      return this.normalizedCheckboxActions.filter(item => item.scope === 'portal');
    },
    verifiedActionOptions() {
      const options = [
        { value: CHECKBOX_NONE_VALUE, text: 'None — award marks on acknowledgement' },
      ];

      this.portalCheckboxActions.forEach((item) => {
        options.push({ value: item.abbr, text: item.label });
      });
      return options;
    },
    checkboxAbbrError() {
      const serverError = this.extractServerFieldError('checkbox_action_abbr');
      return this.checkboxFieldErrors.checkbox_action_abbr || serverError;
    },
    checkboxParamsError() {
      const serverError = this.extractServerFieldError('checkbox_action_params');
      return this.checkboxFieldErrors.checkbox_action_params || serverError;
    },
    checkboxAbbrState() {
      return this.checkboxAbbrError ? false : null;
    },
  },
  watch: {
    task: {
      handler(newVal) {
        this.formData = {
          title: '',
          description: '',
          url: '',
          url_label: '',
          case_study_content: '',
          case_study_questions: '',
          checkbox_action_abbr: CHECKBOX_NONE_VALUE,
          checkbox_action_params: {},
          ...newVal,
        };
        // Ensure completion_mode is always set, default to 'checkbox' if not provided
        if (!this.formData.completion_mode) {
          this.formData.completion_mode = 'checkbox';
        }
        if (this.formData.completion_mode !== 'checkbox') {
          this.clearCheckboxActionFields();
        } else {
          this.formData.checkbox_action_abbr = this.formData.checkbox_action_abbr || CHECKBOX_NONE_VALUE;
          this.formData.checkbox_action_params = this.normalizeActionParams(this.formData.checkbox_action_params);
        }
        this.checkboxFieldErrors.checkbox_action_abbr = '';
        this.checkboxFieldErrors.checkbox_action_params = '';
        this.ensureOptionsForSelectedAction();
        // Force re-render editor when task changes
        this.$nextTick(() => {
          this.editorKey += 1;
        });
      },
      immediate: true,
      deep: true,
    },
    'formData.completion_mode'(newMode, oldMode) {
      if (!oldMode || newMode === oldMode) return;
      if (newMode === 'checkbox') {
        this.formData.rubric = '';
        this.formData.checkbox_action_abbr = CHECKBOX_NONE_VALUE;
        this.formData.checkbox_action_params = {};
        this.checkboxFieldErrors.checkbox_action_abbr = '';
        this.checkboxFieldErrors.checkbox_action_params = '';
      } else {
        this.clearCheckboxActionFields();
      }
    },
    selectedRequiredParams: {
      handler() {
        this.ensureOptionsForSelectedAction();
      },
      immediate: true,
    },
    assignmentType: {
      handler(value) {
        if (value === 'scenario') {
          this.formData.completion_mode = 'scenario';
          this.clearCheckboxActionFields();
          this.fetchScenarioInterviews();
        } else if (this.formData.completion_mode === 'scenario') {
          this.formData.completion_mode = 'checkbox';
          this.formData.simulation = null;
        }
      },
      immediate: true,
    },
    programId() {
      if (this.isScenarioTask) this.fetchScenarioInterviews();
    },
  },
  mounted() {
    this.fetchCheckboxActions();
    if (this.isScenarioTask) this.fetchScenarioInterviews();
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === this.modalId) {
        // Reset form data when modal is shown
        if (this.task) {
          this.formData = {
            ...this.formData, // Keep default values
            ...this.task, // Override with task props
            completion_mode: this.task.completion_mode || 'checkbox', // Ensure completion_mode is set
            checkbox_action_abbr: this.task.checkbox_action_abbr || CHECKBOX_NONE_VALUE,
            checkbox_action_params: this.normalizeActionParams(this.task.checkbox_action_params),
          };
          if (this.formData.completion_mode !== 'checkbox') {
            this.clearCheckboxActionFields();
          }
        } else {
          // For new tasks, ensure completion_mode is set to 'checkbox'
          this.formData.completion_mode = 'checkbox';
          this.formData.checkbox_action_abbr = CHECKBOX_NONE_VALUE;
          this.formData.checkbox_action_params = {};
        }
        this.checkboxFieldErrors.checkbox_action_abbr = '';
        this.checkboxFieldErrors.checkbox_action_params = '';
        this.ensureOptionsForSelectedAction();
        // Force re-render editor when modal is shown
        this.$nextTick(() => {
          this.editorKey += 1;
        });
      }
    });
  },
  beforeDestroy() {
    this.$root.$off('bv::modal::show');
  },
  methods: {
    async fetchScenarioInterviews() {
      this.areInterviewsLoading = true;
      try {
        const params = {};
        if (this.programId) params.program = this.programId;
        const res = await this.$store.dispatch('simulations/getSchoolScenarios', params);
        this.scenarioInterviews = res.data.results || res.data;
      } catch (e) {
        this.scenarioInterviews = [];
      }
      this.areInterviewsLoading = false;
    },
    resetModal() {
      this.formData = {
        title: '',
        description: '',
        url: '',
        url_label: '',
        completion_mode: 'checkbox',
        case_study_content: '',
        case_study_questions: '',
        task_marks: 0,
        rubric: '',
        checkbox_action_abbr: CHECKBOX_NONE_VALUE,
        checkbox_action_params: {},
        simulation: null,
      };
      this.isTitleInvalid = false;
      this.simulationInvalid = false;
      this.rubricInvalid = false;
      this.checkboxFieldErrors.checkbox_action_abbr = '';
      this.checkboxFieldErrors.checkbox_action_params = '';
      // Reset the editor
      this.$nextTick(() => {
        this.editorKey += 1;
      });
      this.$emit('reset');
    },
    normalizeActionParams(params) {
      return params && typeof params === 'object' && !Array.isArray(params) ? { ...params } : {};
    },
    clearCheckboxActionFields() {
      this.formData.checkbox_action_abbr = CHECKBOX_NONE_VALUE;
      this.formData.checkbox_action_params = {};
    },
    extractServerFieldError(field) {
      const val = this.serverErrors && this.serverErrors[field];
      if (Array.isArray(val)) return val[0];
      if (typeof val === 'string') return val;
      return '';
    },
    getParamLabel(paramKey) {
      const labels = {
        program_id: 'Program',
        topic: 'Discussion topic',
      };
      return labels[paramKey] || paramKey.replace(/_/g, ' ');
    },
    isKnownParam(paramKey) {
      return Object.prototype.hasOwnProperty.call(this.optionsByParam, paramKey);
    },
    isOptionalParam(paramKey) {
      return this.selectedOptionalParams.includes(paramKey);
    },
    getParamChoiceOptions(paramKey) {
      const choices = this.selectedActionParamChoices[paramKey];
      if (!Array.isArray(choices) || !choices.length) return [];
      return [
        { value: null, text: this.isOptionalParam(paramKey) ? 'Any' : `Select ${this.getParamLabel(paramKey).toLowerCase()}` },
        ...choices.map(choice => ({ value: choice.value, text: choice.label })),
      ];
    },
    getParamOptions(paramKey) {
      return this.optionsByParam[paramKey] || [];
    },
    getProgramSelectOptions() {
      const options = this.getParamOptions('program_id') || [];
      return [
        { value: null, text: 'Select program' },
        ...options.map(opt => ({ value: opt.id, text: opt.label })),
      ];
    },
    getParamValue(paramKey) {
      return this.formData.checkbox_action_params && Object.prototype.hasOwnProperty.call(this.formData.checkbox_action_params, paramKey)
        ? this.formData.checkbox_action_params[paramKey]
        : null;
    },
    setParamValue(paramKey, value) {
      const next = { ...this.normalizeActionParams(this.formData.checkbox_action_params) };
      if (value === null || value === undefined || value === '') {
        delete next[paramKey];
      } else {
        next[paramKey] = value;
      }
      this.formData.checkbox_action_params = next;
      this.checkboxFieldErrors.checkbox_action_params = '';
    },
    handleCheckboxActionChange() {
      this.formData.checkbox_action_params = {};
      this.checkboxFieldErrors.checkbox_action_abbr = '';
      this.checkboxFieldErrors.checkbox_action_params = '';
      this.ensureOptionsForSelectedAction();
    },
    unwrapList(payload) {
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.results)) return payload.results;
      if (payload.data) {
        if (Array.isArray(payload.data)) return payload.data;
        if (Array.isArray(payload.data.results)) return payload.data.results;
      }
      return [];
    },
    async fetchCheckboxActions() {
      if (Array.isArray(CHECKBOX_ACTION_CACHE.items)) {
        this.checkboxActions = CHECKBOX_ACTION_CACHE.items;
        return;
      }
      if (CHECKBOX_ACTION_CACHE.promise) {
        this.isLoadingCheckboxActions = true;
        try {
          const items = await CHECKBOX_ACTION_CACHE.promise;
          this.checkboxActions = items;
        } finally {
          this.isLoadingCheckboxActions = false;
        }
        return;
      }
      this.isLoadingCheckboxActions = true;
      CHECKBOX_ACTION_CACHE.promise = this.$store.dispatch('assignments/getCheckboxActions')
        .then((res) => {
          const items = this.unwrapList(res);
          CHECKBOX_ACTION_CACHE.items = items;
          return items;
        })
        .finally(() => {
          CHECKBOX_ACTION_CACHE.promise = null;
        });
      try {
        this.checkboxActions = await CHECKBOX_ACTION_CACHE.promise;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load checkbox verified actions.' });
      } finally {
        this.isLoadingCheckboxActions = false;
      }
    },
    async ensureOptionsForSelectedAction() {
      const params = this.selectedRequiredParams || [];
      if (!params.length) return;
      await Promise.all(params.map(paramKey => this.loadOptionsForParam(paramKey)));
    },
    async loadOptionsForParam(paramKey) {
      if (!this.isKnownParam(paramKey)) return;
      if ((this.optionsByParam[paramKey] || []).length) return;
      if (this.paramLoadPromises[paramKey]) {
        await this.paramLoadPromises[paramKey];
        return;
      }

      const loadPromise = (async () => {
        this.$set(this.paramLoadingMap, paramKey, true);
        try {
          let response;
          if (paramKey === 'program_id') {
            response = await axios.get('api/programs/', { params: { page_size: 200 } });
          }

          const list = this.unwrapList(response && response.data);
          const options = list.map(item => ({
            id: item.id,
            label: item.title || item.name || item.label || item.id,
          }));

          this.$set(this.optionsByParam, paramKey, options);
        } catch (e) {
          this.makeToast({ variant: 'danger', msg: `Failed to load ${this.getParamLabel(paramKey).toLowerCase()} options.` });
        } finally {
          this.$set(this.paramLoadingMap, paramKey, false);
          this.$set(this.paramLoadPromises, paramKey, null);
        }
      })();

      this.$set(this.paramLoadPromises, paramKey, loadPromise);
      await loadPromise;
    },
    validateCheckboxActionFields() {
      this.checkboxFieldErrors.checkbox_action_abbr = '';
      this.checkboxFieldErrors.checkbox_action_params = '';

      if (this.formData.completion_mode !== 'checkbox') return true;
      if (!this.formData.checkbox_action_abbr) {
        this.formData.checkbox_action_params = {};
        return true;
      }

      if (!this.selectedCheckboxAction) {
        this.checkboxFieldErrors.checkbox_action_abbr = 'Unknown verified action selected.';
        return false;
      }

      const params = this.normalizeActionParams(this.formData.checkbox_action_params);
      const missing = this.selectedRequiredParams.filter((key) => {
        const value = params[key];
        if (value === null || value === undefined) return true;
        if (typeof value === 'string') return !value.trim();
        return false;
      });
      if (missing.length) {
        this.checkboxFieldErrors.checkbox_action_params = `Missing required keys: ${missing.join(', ')}`;
        return false;
      }

      return true;
    },
    validateUrl() {
      if (!this.formData.url) {
        this.urlError = '';
        return true;
      }
      try {
        // Basic URL validation
        new URL(this.formData.url);
        this.urlError = '';
        return true;
      } catch (e) {
        this.urlError = 'Please enter a valid URL (e.g., https://example.com)';
        return false;
      }
    },
    insertRubricTemplate(opt) {
      this.$set(this.formData, 'rubric', (opt && opt.template) || '');
    },
    handleSubmit() {
      if (this.formData.title === '') {
        this.isTitleInvalid = true;
        return;
      }

      // Validate URL if it's provided
      if (this.formData.url && !this.validateUrl()) {
        return;
      }

      if (this.isScenarioTask) {
        this.formData.completion_mode = 'scenario';
        this.simulationInvalid = !this.formData.simulation;
        if (this.simulationInvalid) return;
      }

      // Rubric is required for non-checkbox tasks when AI grading is enabled
      const needsRubric =
        this.isGradedAssignment && this.isAiGradingEnabled && this.formData.completion_mode !== 'checkbox';
      this.rubricInvalid = needsRubric && !String(this.formData.rubric || '').trim();
      if (this.rubricInvalid) return;

      if (!this.validateCheckboxActionFields()) return;

      if (this.formData.completion_mode !== 'checkbox') {
        this.clearCheckboxActionFields();
      }

      if (!this.$v.formData.$invalid) {
        this.isTitleInvalid = false;
        this.isSubmitting = true;
        const selectedInterview = this.scenarioInterviews.find(item => item.id === this.formData.simulation);
        this.$emit('submit', {
          ...this.formData,
          checkbox_action_abbr: this.formData.checkbox_action_abbr || CHECKBOX_NONE_VALUE,
          checkbox_action_params: this.normalizeActionParams(this.formData.checkbox_action_params),
          simulation: this.isScenarioTask ? this.formData.simulation : null,
          simulation_title: selectedInterview ? selectedInterview.title : '',
        });
        this.$bvModal.hide(this.modalId);
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.tox-tinymce {
  border-radius: 0.25rem;
  border: 1px solid #dae1e7;
}
.monospace-textarea {
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace;
  font-size: 0.85rem;
}
</style>
